(() => {
  if (window.ryppleCartFeedbackBound) return;
  window.ryppleCartFeedbackBound = true;

  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const feedbackClass = 'rp-cart-value-updated';
  let drawerRefreshController;
  let pageRefreshController;

  const refreshClosedDrawer = async (cartData) => {
    const drawer = document.querySelector('cart-drawer.rp-cart-drawer');
    if (!drawer || !document.getElementById('main-cart-items')) return;

    drawerRefreshController?.abort();
    const controller = new AbortController();
    drawerRefreshController = controller;

    try {
      // The drawer is rendered by the layout, not a Shopify section. Fetch the
      // cart page once after a page-cart edit so its markup matches the new cart.
      let drawerMarkup = '';
      {
        const cartUrl = window.routes?.cart_url || '/cart';
        const url = new URL(cartUrl, window.location.origin);
        const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal
        });
        if (!response.ok) return;
        drawerMarkup = await response.text();
      }

      const html = new DOMParser().parseFromString(drawerMarkup, 'text/html');
      const freshDrawer = html.querySelector('cart-drawer');
      const freshInner = freshDrawer?.querySelector('.drawer__inner');
      const currentInner = drawer.querySelector('.drawer__inner');
      if (controller.signal.aborted || !freshInner || !currentInner) return;

      const focusId = drawer.contains(document.activeElement) ? document.activeElement.id : '';
      const scrollTop = currentInner.scrollTop;
      drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      drawer.classList.toggle('rp-cart-drawer', freshDrawer.classList.contains('rp-cart-drawer'));
      currentInner.replaceWith(freshInner);
      freshInner.scrollTop = scrollTop;
      if (focusId) (document.getElementById(focusId) || freshInner.querySelector('.drawer__close'))?.focus();
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Cart drawer refresh failed', error);
    }
  };

  const animateValue = (element) => {
    if (!element || reducedMotion?.matches) return;
    element.classList.remove(feedbackClass);
    requestAnimationFrame(() => {
      if (element.isConnected) element.classList.add(feedbackClass);
    });
  };

  const refreshCartPage = async (cartData, variantId) => {
    const cartItems = document.querySelector('cart-items');
    const cartFooter = document.getElementById('main-cart-footer');
    if (!cartItems || !cartFooter) return;

    const itemsId = document.getElementById('main-cart-items')?.dataset.id;
    const footerId = cartFooter.dataset.id;
    if (!itemsId || !footerId) return;
    const optionalSections = [
      {
        element: document.querySelector('[id^="shopify-section-"][id$="__rypple-cart-timer"], #shopify-section-rypple-cart-timer'),
        selector: '.rp-cart-page-timer'
      },
      {
        element: document.querySelector('[id^="shopify-section-"][id$="__rypple-cart-trust"], #shopify-section-rypple-cart-trust'),
        selector: '.rp-cart-page-extras'
      }
    ];
    const sectionIds = [itemsId, footerId, ...optionalSections.map(({ element }) => element?.id.replace('shopify-section-', '')).filter(Boolean)];

    pageRefreshController?.abort();
    const controller = new AbortController();
    pageRefreshController = controller;

    try {
      let sections = cartData.sections;
      if (!sectionIds.every((id) => sections?.[id])) {
        const url = new URL(window.routes?.cart_url || '/cart', window.location.origin);
        url.searchParams.set('sections', sectionIds.join(','));
        const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal
        });
        if (!response.ok) return;
        sections = await response.json();
      }
      if (controller.signal.aborted) return;

      const parser = new DOMParser();
      const itemsHtml = parser.parseFromString(sections[itemsId], 'text/html');
      const footerHtml = parser.parseFromString(sections[footerId], 'text/html');
      const freshItems = itemsHtml.querySelector('cart-items');
      const freshContents = itemsHtml.querySelector('#main-cart-items .js-contents');
      const currentContents = cartItems.querySelector('#main-cart-items .js-contents');
      const freshFooter = footerHtml.getElementById('main-cart-footer');
      const freshFooterContents = freshFooter?.querySelector('.js-contents');
      const currentFooterContents = cartFooter.querySelector('.js-contents');
      if (!freshItems || !freshContents || !currentContents || !freshFooterContents || !currentFooterContents) return;

      // Keep the existing cart-items custom element and its event listeners.
      currentContents.innerHTML = freshContents.innerHTML;
      currentFooterContents.innerHTML = freshFooterContents.innerHTML;
      cartItems.classList.toggle('is-empty', freshItems.classList.contains('is-empty'));
      cartFooter.classList.toggle('is-empty', freshFooter.classList.contains('is-empty'));

      // The RYPPLE extras live outside the cart sections. Only swap their
      // content when the device enters or leaves the cart.
      for (const { element: currentSection, selector } of optionalSections) {
        if (!currentSection) continue;
        const id = currentSection.id.replace('shopify-section-', '');
        if (!sections[id]) continue;
        const freshSection = parser.parseFromString(sections[id], 'text/html').getElementById(currentSection.id);
        if (currentSection && freshSection && Boolean(currentSection.querySelector(selector)) !== Boolean(freshSection.querySelector(selector))) {
          currentSection.innerHTML = freshSection.innerHTML;
        }
      }

      animateValue(cartFooter.querySelector('.rp-cart-total__now'));
      if (variantId) {
        const quantity = [...cartItems.querySelectorAll('.quantity__input[data-quantity-variant-id]')]
          .find((input) => input.dataset.quantityVariantId === String(variantId));
        animateValue(quantity);
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Cart page refresh failed', error);
    }
  };

  const bind = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source, cartData, variantId } = {}) => {
      // The theme publishes this after a successful /cart/change.js response
      // and after replacing the cart section HTML. Failed updates never arrive here.
      if (source !== 'cart-items' || !cartData) return;

      const openDrawer = document.querySelector('cart-drawer.rp-cart-drawer.active');
      if (openDrawer) refreshCartPage(cartData, variantId);
      else refreshClosedDrawer(cartData);
      const cartRoot = openDrawer || document.getElementById('main-cart-items');
      const total = openDrawer?.querySelector('.rp-cart-total__now')
        || document.querySelector('#main-cart-footer .rp-cart-total__now');
      if (!total) return;

      animateValue(total);
      animateValue(openDrawer?.querySelector('.rp-cart-count'));
      animateValue(document.querySelector('#cart-icon-bubble .cart-count-bubble'));

      if (variantId && cartRoot) {
        const quantity = [...cartRoot.querySelectorAll('.quantity__input[data-quantity-variant-id]')]
          .find((input) => input.dataset.quantityVariantId === String(variantId));
        animateValue(quantity);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
})();
